<template>
  <div style="min-height: calc(100vh - 176px)" class="flex flex-wrap mt-4">
    <div 
      class="relative py-2 px-2 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0" 
      style="min-height: calc(-176px + 100vh);"
    >
      <div class="bg-white shadow-md p-4 rounded-lg flex flex-wrap gap-4 mb-4">
        <input
          type="text"
          v-model="searchQuery"
          @input="updateFilters"
          placeholder="Поиск по названию..."
          class="border p-2 rounded w-full sm:w-1/3"
        />
        <select
          v-model="sortField"
          @change="updateFilters"
          class="border px-3 py-2 pr-7 rounded whitespace-nowrap"
        >
          <option value="name">По названию {{ sortOrder === 'asc' ? '↑' : '↓' }}</option>
        </select>
        <button
          @click="toggleSortOrder"
          class="border px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 whitespace-nowrap"
        >
          {{ sortOrder === 'asc' ? '↑ Возрастание' : '↓ Убывание' }}
        </button>
        <select
          v-model="selectedStatus"
          @change="updateFilters"
          class="border p-2 rounded w-full sm:w-1/4"
        >
          <option value="active">Не удаленные</option>
          <option value="deleted">Удаленные</option>
          <option value="all">Все</option>
        </select>
        <router-link
          :to="{
            name: 'CategoryCreate',
            query: {
              content_type: $route.params.contentType,
            },
          }"
          class="bg-blue-500 text-white px-4 py-2 rounded"
        >
          Создать
        </router-link>
      </div>

      <div class="bg-white shadow-md rounded-lg overflow-hidden">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-200">
              <th class="p-3">#</th>
              <th class="p-3">Название</th>
              <th class="p-3">Slug</th>
              <th class="p-3">Статус</th>
              <th class="p-3">Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(category, index) in categoriesList" 
              :key="category.id" 
              class="border-t"
            >
              <td class="p-3">{{ index + 1 + (currentPage - 1) * perPage }}</td>
              <td class="p-3">
                <router-link 
                  :to="`/admin/categories/edit/${$route.params.contentType}/${category.id}`" 
                  class="text-blue-600 hover:underline"
                >
                  {{ category.name }}
                </router-link>
              </td>
              <td class="p-3">{{ category.slug }}</td>
              <td class="p-3">
                <span :class="category.is_delete ? 'text-red-600' : 'text-green-600'">
                  {{ category.is_delete ? 'Удалена' : 'Активна' }}
                </span>
              </td>
              <td class="p-3 flex gap-2 items-center">
                <button 
                  v-if="!category.is_delete" 
                  @click="deleteCategory(category.id)" 
                  class="bg-red-500 text-white px-3 py-1 rounded"
                >
                  Удалить категорию
                </button>
                <button 
                  v-if="category.is_delete" 
                  @click="restoreCategory(category.id)" 
                  class="bg-green-500 text-white px-3 py-1 rounded"
                >
                  Восстановить категорию
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-center mt-4" v-if="totalPages > 1">
        <button 
          @click="changePage(currentPage - 1)" 
          :disabled="currentPage === 1" 
          class="px-4 py-2 bg-gray-300 rounded mr-2"
        >
          ← Назад
        </button>
        <span class="px-4 py-2">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button 
          @click="changePage(currentPage + 1)" 
          :disabled="currentPage >= totalPages" 
          class="px-4 py-2 bg-gray-300 rounded ml-2"
        >
          Вперед →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_CATEGORIES, HEADERS_TOKEN_JSON, DEFAULT_PER_PAGE } from "@/const/uri";

export default {
  data() {
    return {
      categories: [],
      searchQuery: "",
      selectedStatus: "active",
      currentPage: 1,
      perPage: DEFAULT_PER_PAGE,
      totalPages: 1,
      sortField: "name",
      sortOrder: "asc",
    };
  },
  computed: {
    categoriesList() {
      return this.categories;
    },
  },
  mounted() {
    this.fetchCategories();
  },
  watch: {
    $route: {
      handler() {
        this.fetchCategories();
      },
      immediate: true,
    },
  },
  methods: {
    async fetchCategories() {
      const contentType = this.$route.params.contentType;
      let is_delete_param;
      if (this.selectedStatus === "active") {
        is_delete_param = "false";
      } else if (this.selectedStatus === "deleted") {
        is_delete_param = "true";
      } else {
        is_delete_param = undefined;
      }

      const orderingField = this.sortOrder === 'desc' ? `-${this.sortField}` : this.sortField;

      try {
        const response = await axios({
          method: "GET",
          url: API_CATEGORIES.list,
          headers: HEADERS_TOKEN_JSON(),
          params: {
            content_type: contentType,
            search: this.searchQuery || undefined,
            p: this.currentPage,
            page_size: this.perPage,
            is_delete: is_delete_param,
            ordering: orderingField,
          },
        });
        if (response.data.code === 0) {
          const responseData = response.data.data;
          this.categories = responseData.results;
          this.totalPages = Math.ceil(responseData.count / this.perPage);
        }
      } catch (error) {
        console.log("Ошибка загрузки категорий:", error);
      }
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.updateFilters();
    },
    updateFilters() {
      this.currentPage = 1;
      this.fetchCategories();
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchCategories();
      }
    },
    async deleteCategory(id) {
      if (confirm("Вы уверены, что хотите удалить эту категорию?")) {
        try {
          await axios.delete(`${API_CATEGORIES.base}${id}/`, {
            headers: HEADERS_TOKEN_JSON(),
            params: {
              content_type: this.$route.params.contentType,
            },
          });
          this.fetchCategories();
        } catch (error) {
          console.error("Ошибка удаления категории:", error);
        }
      }
    },
    async restoreCategory(id) {
      if (confirm("Вы уверены, что хотите восстановить эту категорию?")) {
        try {
          await axios.post(`${API_CATEGORIES.base}${id}/restore/`, {}, {
            headers: HEADERS_TOKEN_JSON(),
            params: {
              content_type: this.$route.params.contentType,
            },
          });
          this.fetchCategories();
        } catch (error) {
          console.error("Ошибка восстановления категории:", error);
        }
      }
    },
  },
};
</script>
